import type { OpeningHours, OpeningPeriod } from '../types';

/**
 * Etat ouvert / ferme, calcule a chaque rendu.
 *
 * Jamais stocke : un booleen "ouvert" fige en base serait faux quelques
 * minutes plus tard. Le calcul se fait ici, cote client, avec l'heure locale
 * de celui qui regarde — c'est bien "est-ce que je peux appeler maintenant ?"
 * que l'on veut repondre, pas l'etat au moment de la derniere recherche.
 *
 * Aucune estimation : sans horaires Google, l'etat reste UNKNOWN.
 */
export type OpenState = 'OPEN' | 'CLOSED' | 'UNKNOWN';

export interface OpenStatus {
  state: OpenState;
  /** Etablissement declare ouvert en continu (24/7). */
  alwaysOpen: boolean;
  /** Heure de fermeture ("19:00") quand l'etablissement est ouvert. */
  closesAt: string | null;
  /** Prochaine ouverture ("09:00") quand il est ferme. */
  opensAt: string | null;
}

const MINUTES_PER_DAY = 24 * 60;
const MINUTES_PER_WEEK = 7 * MINUTES_PER_DAY;

const CLOSED_UNKNOWN: OpenStatus = {
  state: 'UNKNOWN',
  alwaysOpen: false,
  closesAt: null,
  opensAt: null,
};

function formatMinutes(minutes: number): string {
  const normalized = ((minutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  const hours = Math.floor(normalized / 60);
  const rest = normalized % 60;
  return `${String(hours).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;
}

/** Minutes ecoulees depuis dimanche 00:00, dans le fuseau du navigateur. */
export function weekMinutes(date: Date): number {
  return date.getDay() * MINUTES_PER_DAY + date.getHours() * 60 + date.getMinutes();
}

interface Interval {
  start: number;
  end: number;
}

/**
 * Convertit un creneau en intervalle sur la semaine.
 * Un creneau qui se termine avant de commencer traverse minuit : sa fin part
 * dans la semaine suivante (vendredi 22h -> samedi 2h).
 */
function toInterval(period: OpeningPeriod): Interval | null {
  if (period.closeDay === null || period.closeMinutes === null) return null;

  const start = period.openDay * MINUTES_PER_DAY + period.openMinutes;
  let end = period.closeDay * MINUTES_PER_DAY + period.closeMinutes;
  if (end <= start) end += MINUTES_PER_WEEK;

  return { start, end };
}

export function getOpenStatus(
  hours: OpeningHours | null | undefined,
  now: Date = new Date(),
): OpenStatus {
  if (!hours || hours.periods.length === 0) return CLOSED_UNKNOWN;

  // Google signale le 24/7 par un creneau sans heure de fermeture.
  if (hours.periods.some((period) => period.closeDay === null || period.closeMinutes === null)) {
    return { state: 'OPEN', alwaysOpen: true, closesAt: null, opensAt: null };
  }

  const current = weekMinutes(now);
  const intervals = hours.periods
    .map(toInterval)
    .filter((interval): interval is Interval => interval !== null);

  if (intervals.length === 0) return CLOSED_UNKNOWN;

  for (const { start, end } of intervals) {
    // La deuxieme comparaison rattrape les creneaux commences la semaine
    // precedente et qui debordent sur dimanche matin.
    const inside =
      (current >= start && current < end) ||
      (current + MINUTES_PER_WEEK >= start && current + MINUTES_PER_WEEK < end);

    if (inside) {
      return { state: 'OPEN', alwaysOpen: false, closesAt: formatMinutes(end), opensAt: null };
    }
  }

  let soonest = Number.POSITIVE_INFINITY;
  let nextStart = 0;
  for (const { start } of intervals) {
    const delay = (start - current + MINUTES_PER_WEEK) % MINUTES_PER_WEEK;
    if (delay < soonest) {
      soonest = delay;
      nextStart = start;
    }
  }

  return {
    state: 'CLOSED',
    alwaysOpen: false,
    closesAt: null,
    opensAt: Number.isFinite(soonest) ? formatMinutes(nextStart) : null,
  };
}

/** Libelle compact affiche dans les listes. */
export function formatOpenStatus(status: OpenStatus): string {
  if (status.state === 'UNKNOWN') return 'Horaires inconnus';
  if (status.alwaysOpen) return 'Ouvert 24h/24';
  if (status.state === 'OPEN') {
    return status.closesAt === null ? 'Ouvert' : `Ouvert - ferme a ${status.closesAt}`;
  }
  return status.opensAt === null ? 'Ferme' : `Ferme - ouvre a ${status.opensAt}`;
}
